import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Phone, ShieldAlert, Flame, Mountain, Waves, ChevronRight } from "lucide-react";

const CONTACTS = [
  { name: "BNPB", role: "Badan Nasional Penanggulangan Bencana", number: "117", badge: "Bebas Pulsa" },
  { name: "BASARNAS", role: "Pencarian & Pertolongan", number: "115", badge: "24 Jam" },
  { name: "Ambulans / Kemenkes", role: "Layanan Medis Darurat", number: "118 / 119", badge: "Darurat" },
  { name: "Kepolisian RI", role: "POLRI", number: "110", badge: "Polisi" },
  { name: "Pemadam Kebakaran", role: "Damkar", number: "113", badge: "Kebakaran" },
];

const GUIDES = [
  {
    icon: ShieldAlert,
    color: "text-red-400",
    title: "Saat Gempa Bumi",
    steps: [
      "Lindungi kepala dan leher — segera berlindung di bawah meja kokoh (Drop, Cover, Hold).",
      "Jauhi jendela, kaca, dan benda tergantung. Matikan kompor jika memungkinkan.",
      "Jika di luar, cari lapangan terbuka jauh dari bangunan dan tiang listrik.",
      "Jangan gunakan lift. Gunakan tangga darurat setelah guncangan berhenti.",
    ],
  },
  {
    icon: Waves,
    color: "text-cyan-400",
    title: "Peringatan Tsunami",
    steps: [
      "Segera evakuasi ke dataran tinggi (>20 m) atau menjauh dari pantai (>1 km).",
      "Jangan tunggu sirine berbunyi — gempa besar dekat pantai = potensi tsunami.",
      "Ikuti jalur evakuasi resmi (tanda merah). Prioritaskan lansia, anak, disabilitas.",
      "Tetap di zona aman minimal 24 jam. Waspada gelombang susulan.",
    ],
  },
  {
    icon: Mountain,
    color: "text-amber-400",
    title: "Erupsi Gunung Api",
    steps: [
      "Pantau status gunung dari PVMBG / Magma Indonesia.",
      "Gunakan masker N95 saat abu vulkanik turun. Lindungi mata dengan kacamata.",
      "Ikuti radius KRB (Kawasan Rawan Bencana) — jangan mendekati puncak.",
      "Simpan air bersih; abu vulkanik dapat mencemari sumber air.",
    ],
  },
  {
    icon: Flame,
    color: "text-orange-400",
    title: "Kebakaran Hutan (Karhutla)",
    steps: [
      "Batasi aktivitas luar ruangan saat ISPU tinggi. Gunakan masker N95.",
      "Tutup ventilasi rumah. Pasang penjernih udara bila memungkinkan.",
      "Laporkan titik api ke Manggala Agni / Damkar 113.",
      "Jangan membuka lahan dengan pembakaran (dilarang UU 32/2009).",
    ],
  },
];

export default function MitigationModal({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-3xl max-h-[85vh] overflow-y-auto border-2 border-slate-700 bg-slate-900 text-slate-100 shadow-2xl shadow-cyan-500/10" data-testid="mitigation-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold tracking-tight text-slate-50">Panduan Siaga & Mitigasi Bencana</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <section>
            <h3 className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-400">Kontak Darurat Resmi Indonesia</h3>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {CONTACTS.map((c) => (
                <a
                  key={c.name}
                  href={`tel:${c.number.split(" ")[0]}`}
                  className="group flex items-center justify-between rounded-xl border border-slate-800 bg-slate-900/60 p-3 hover:border-cyan-500/50 hover:bg-slate-900"
                  data-testid={`emergency-${c.number.split(" ")[0]}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="rounded-lg bg-red-500/10 p-2 text-red-400 group-hover:bg-red-500/20">
                      <Phone className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-100">{c.name}</div>
                      <div className="text-[11px] text-slate-400">{c.role}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-lg font-bold text-cyan-400">{c.number}</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">{c.badge}</div>
                  </div>
                </a>
              ))}
            </div>
          </section>

          <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {GUIDES.map((g) => (
              <div key={g.title} className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
                <div className="mb-3 flex items-center gap-2">
                  <g.icon className={`h-5 w-5 ${g.color}`} />
                  <h4 className="text-sm font-bold text-slate-100">{g.title}</h4>
                </div>
                <ul className="space-y-2 text-xs text-slate-300">
                  {g.steps.map((s, i) => (
                    <li key={i} className="flex gap-2">
                      <ChevronRight className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${g.color}`} />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
